import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type {
  ICommunityLocationPickerV2,
  ISpotLocationPickerV2
} from 'type/feed'
import type {
  IHistoryListItem,
  ISearchCommunityV2,
  ISearchHistoryV2,
  ISearchKeywordV2,
  ISearchUserV2
} from 'type/search'

const SEARCH_LIMIT = 6

interface IRecentSearchState {
  recentSearch: ISearchHistoryV2[]
}
interface IRecentSearchActions {
  addSearch: (item: IRecentSearchState['recentSearch'][number]) => void
  removeSearch: (id: string, type: string) => void
}
export const useRecentSearchStore = create<
  IRecentSearchState & IRecentSearchActions
>()(
  persist(
    (set, get) => ({
      recentSearch: [] as ISearchHistoryV2[],
      addSearch: item => {
        const { recentSearch } = get()

        let newRecentSearch = recentSearch.filter(
          recent => recent.id !== item.id || recent.type !== item.type
        )
        newRecentSearch = [item, ...newRecentSearch].slice(0, SEARCH_LIMIT)

        set({ recentSearch: newRecentSearch })
      },
      removeSearch: (id, type) => {
        const { recentSearch } = get()
        const newRecentSearch = recentSearch.filter(
          item => item.id !== id || item.type !== type
        )
        set({ recentSearch: newRecentSearch })
      }
    }),
    {
      name: 'search-recentSearch',
      version: 1,
      migrate: (persistedState, version) => {
        const oldSearch = (persistedState as any).recentSearch

        const newSearch =
          version === 1
            ? oldSearch
            : (oldSearch.map((item: IHistoryListItem) => {
                if (item.community_id) {
                  return {
                    type: 'community',
                    id: item.community_id,
                    title: item.title,
                    count: item.count,
                    image: ''
                  } satisfies ISearchCommunityV2
                } else if (item.user_id) {
                  return {
                    type: 'user',
                    id: item.user_id,
                    uid: item.uid,
                    name: item.name,
                    image: item.head_shot?.small_headshot_url || ''
                  } satisfies ISearchUserV2
                } else {
                  return {
                    type: 'keyword',
                    id: item.value,
                    keyword: item.value
                  } satisfies ISearchKeywordV2
                }
              }) satisfies ISearchHistoryV2[])

        ;(persistedState as any).recentSearch = newSearch
        return persistedState as any
      }
    }
  )
)

const createRecentLocations = <
  T extends ISpotLocationPickerV2 | ICommunityLocationPickerV2
>(
  key: string
) => {
  interface IRecentLocationsState {
    recentLocations: T[]
  }
  interface IRecentLocationsActions {
    addRecentLocation: (location: T) => void
    removeRecentLocation: (index: number) => void
  }

  return create<IRecentLocationsState & IRecentLocationsActions>()(
    persist(
      (set, get) => ({
        recentLocations: [] as T[],
        addRecentLocation: (location: T) => {
          let { recentLocations } = get()
          if (!('place' in location) && !location.location) return

          const contentId = location.content_ids ? location.content_ids[0] : ''

          const { contentIdsIndex } = recentLocations.reduce(
            (recent, location, index) => {
              const placeId = 'place' in location ? location.place?.id : ''
              const locationId = location.location?.id
              const contentId = location?.content_ids
                ? location.content_ids[0]
                : ''

              if (placeId) {
                recent.placeIdIndex[placeId] = index
              }
              if (locationId) {
                recent.locationIdIndex[locationId] = index
              }
              if (contentId) {
                recent.contentIdsIndex[contentId] = index
              }
              return recent
            },
            {
              placeIdIndex: {} as Record<string, number>,
              locationIdIndex: {} as Record<string, number>,
              contentIdsIndex: {} as Record<string, number>
            }
          )

          const newRecentLocations = recentLocations

          // if existed, remove it

          if (contentId && contentIdsIndex[contentId] !== undefined) {
            newRecentLocations.splice(contentIdsIndex[contentId], 1)
          }

          set({
            // add to the top, and pick the first 3
            recentLocations: [location, ...newRecentLocations].slice(0, 3)
          })
        },
        removeRecentLocation: index => {
          const { recentLocations } = get()
          const newRecentLocations = recentLocations.filter(
            (_, i) => i !== index
          )
          set({ recentLocations: newRecentLocations })
        }
      }),
      {
        name: key,
        version: 1,
        migrate: (persistedState, version) => {
          const oldRecentLocations = (persistedState as any).recentLocations
          const newRecentLocations = version === 1 ? oldRecentLocations : []

          ;(persistedState as any).recentLocations = newRecentLocations
          return persistedState as any
        }
      }
    )
  )
}
export const useSpotRecentLocationsStore =
  createRecentLocations<ISpotLocationPickerV2>('homefeed-recentLocations')
export const useCommunityRecentLocationsStore =
  createRecentLocations<ICommunityLocationPickerV2>('community-recentLocations')
